import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/auth/tokens';
import { refreshAccessToken } from '@/auth/refresh';
import { Icon } from './Icon';
import s from './ToastHost.module.css';
import { AUTHORIZATION_DENIED_EVENT } from '@/auth/authorizationEvents';
const AUTODISMISS_MS = 7_000;
// Must stay >= the CSS .toast[data-state='closing'] animation duration.
// Bumping the CSS alone is fine; bumping this alone leaves dead frames.
const EXIT_MS = 260;
const MAX_VISIBLE = 5;
// Reconnect backoff schedule (ms). After the 5th retry stays at 30s — that's
// "the network is gone; we'll bounce when it returns". Backoff resets to 0 on
// a successful open.
const RECONNECT_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];
// If the stream dies within this window of opening, treat the failure as
// likely auth-related (expired access_token) and try one token refresh
// before continuing the backoff.
const QUICK_FAIL_WINDOW_MS = 2_000;
/**
 * Listens to /api/v1/notifications/stream (SSE), shows toast cards in the
 * bottom-right corner, and invalidates the notifications queries so the
 * `/notifications` page + bell badge update in lockstep.
 *
 * Auth: EventSource doesn't allow custom headers; we pass the Bearer JWT
 * as ?access_token=. The backend's auth middleware accepts it for this
 * single path.
 */
export function ToastHost() {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Map());
    const nav = useNavigate();
    const qc = useQueryClient();
    // Stable: empty deps + functional setState reads, so the SSE handler can
    // bind it once for the autodismiss timer without recreating subscriptions.
    const dismiss = useCallback((id) => {
        const t = timers.current.get(id);
        if (t) {
            clearTimeout(t);
            timers.current.delete(id);
        }
        setToasts((cur) => {
            // No-op if the toast is already closing or already gone — protects
            // against double-fire from autodismiss + manual close racing.
            if (!cur.some((x) => x.id === id && x.state === 'open'))
                return cur;
            return cur.map((x) => x.id === id ? { ...x, state: 'closing' } : x);
        });
        window.setTimeout(() => {
            setToasts((cur) => cur.filter((x) => x.id !== id));
        }, EXIT_MS);
    }, []);
    const show = useCallback((payload) => {
        const incoming = { ...payload, state: 'open' };
        setToasts((cur) => {
            const next = [...cur.filter((item) => item.id !== incoming.id), incoming];
            return next.length > MAX_VISIBLE ? next.slice(-MAX_VISIBLE) : next;
        });
        const previous = timers.current.get(payload.id);
        if (previous)
            window.clearTimeout(previous);
        const timer = window.setTimeout(() => dismiss(payload.id), AUTODISMISS_MS);
        timers.current.set(payload.id, timer);
    }, [dismiss]);
    useEffect(() => {
        const onDenied = () => {
            show({
                id: 'authorization-denied',
                severity: 'error',
                title: 'Недостаточно прав для этого действия',
                event: 'authorization_denied',
            });
        };
        window.addEventListener(AUTHORIZATION_DENIED_EVENT, onDenied);
        return () => window.removeEventListener(AUTHORIZATION_DENIED_EVENT, onDenied);
    }, [show]);
    useEffect(() => {
        // SSE lifecycle with exponential backoff + one-shot token refresh on
        // quick-fail. Native EventSource auto-reconnects on transport drop, but
        // ignores the auth path entirely: an expired ?access_token= keeps
        // hammering /stream with 401s. We layer manual reconnect on top so we
        // can refresh-then-resume, and so we don't busy-loop on broken networks.
        let es = null;
        let reconnectTimer = null;
        let attempt = 0;
        let openedAt = 0;
        let didTryRefresh = false;
        let cancelled = false;
        const handleNotification = (e) => {
            let payload = null;
            try {
                payload = JSON.parse(e.data);
            }
            catch {
                return;
            }
            show(payload);
            // Refresh badge + list.
            qc.invalidateQueries({ queryKey: ['notifications'] });
        };
        const connect = () => {
            if (cancelled)
                return;
            const token = getAccessToken();
            if (!token)
                return; // logged out — wait for tokens to reappear via remount
            const url = `/api/v1/notifications/stream?access_token=${encodeURIComponent(token)}`;
            es = new EventSource(url);
            openedAt = Date.now();
            es.addEventListener('notification', handleNotification);
            es.onopen = () => {
                // Successful handshake → reset backoff so a transient drop doesn't
                // inherit the previous-attempt's delay.
                attempt = 0;
                didTryRefresh = false;
            };
            es.onerror = () => {
                // EventSource enters CLOSED on auth failure (the server drops a 401);
                // on network drops it enters CONNECTING and tries to retry by itself.
                // We unconditionally tear it down and own the retry so we can layer
                // token-refresh and bounded backoff.
                const wasQuick = Date.now() - openedAt < QUICK_FAIL_WINDOW_MS;
                const closedSrc = es;
                es = null;
                if (closedSrc) {
                    closedSrc.onopen = null;
                    closedSrc.onerror = null;
                    closedSrc.close();
                }
                scheduleReconnect(wasQuick);
            };
        };
        const scheduleReconnect = (wasQuick) => {
            if (cancelled || reconnectTimer !== null)
                return;
            // If the stream died right after opening, the access_token is most
            // likely expired. Try one refresh before continuing the normal backoff
            // — usually fixes it without a single user-visible delay.
            if (wasQuick && !didTryRefresh) {
                didTryRefresh = true;
                void refreshAccessToken().then((newToken) => {
                    if (cancelled)
                        return;
                    if (newToken) {
                        attempt = 0;
                        connect();
                    }
                    else {
                        // Refresh failed: fall through to normal backoff.
                        scheduleReconnectAfter(RECONNECT_BACKOFF_MS[attempt] ?? 30_000);
                        attempt = Math.min(attempt + 1, RECONNECT_BACKOFF_MS.length - 1);
                    }
                });
                return;
            }
            const delay = RECONNECT_BACKOFF_MS[attempt] ?? 30_000;
            attempt = Math.min(attempt + 1, RECONNECT_BACKOFF_MS.length - 1);
            scheduleReconnectAfter(delay);
        };
        const scheduleReconnectAfter = (delay) => {
            reconnectTimer = window.setTimeout(() => {
                reconnectTimer = null;
                connect();
            }, delay);
        };
        // When the tab becomes visible again after being hidden, browsers may
        // hold an SSE connection in an indeterminate state. Force a fresh
        // connection on visibility-resume.
        const onVisibility = () => {
            if (document.visibilityState === 'visible' && !es && reconnectTimer === null) {
                attempt = 0;
                connect();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        connect();
        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisibility);
            if (reconnectTimer !== null) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            if (es) {
                es.onopen = null;
                es.onerror = null;
                es.close();
                es = null;
            }
            for (const t of timers.current.values())
                clearTimeout(t);
            timers.current.clear();
        };
    }, [qc, show]);
    const openDeep = (t) => {
        if (t.event === 'authorization_denied') {
            dismiss(t.id);
            return;
        }
        // Best-effort deep links by target_type. Falls back to /notifications.
        if (t.target_type === 'backup' && t.target_id) {
            nav(`/backups`);
        }
        else if (t.target_type === 'database' && t.target_id) {
            nav(`/databases/${t.target_id}`);
        }
        else if (t.target_type === 'storage') {
            nav(`/settings/storages`);
        }
        else {
            nav('/notifications');
        }
        dismiss(t.id);
    };
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { className: s.host, role: "region", "aria-label": "Notifications", children: toasts.map((t) => (_jsxs("div", { className: `${s.toast} ${s[t.severity] ?? ''}`, "data-state": t.state, onClick: () => openDeep(t), role: "button", tabIndex: 0, onKeyDown: (e) => {
                if (e.key === 'Enter')
                    openDeep(t);
            }, children: [_jsx("span", { className: s.icon, "aria-hidden": "true", children: _jsx(Icon, { name: t.severity === 'error' ? 'x' : t.severity === 'warn' ? 'warn' : 'info', size: 14 }) }), _jsxs("div", { className: s.body, children: [_jsx("div", { className: s.title, children: t.title }), t.body && _jsx("div", { className: s.detail, children: t.body })] }), _jsx("button", { type: "button", className: s.close, onClick: (e) => {
                        e.stopPropagation();
                        dismiss(t.id);
                    }, "aria-label": "Dismiss", children: _jsx(Icon, { name: "x", size: 12 }) })] }, t.id))) }));
}
